import { useEffect, useState } from "react";
import { api } from "../../api/client";
import {
  Badge,
  Button,
  DataCard,
  Field,
  Icon,
  Input,
  Modal,
  PageTitle,
  Spinner,
  Td,
  Th
} from "../../components/ui";

// Halaman Kelola Cabang — HANYA untuk Super Admin (di-gate di Shell).
export default function BranchesPage({ setToast }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {} = buat baru; objek = edit

  async function load() {
    setLoading(true);
    try {
      const res = await api.getAllBranchesAdmin();
      if (res.success) setRows(res.data || []);
    } catch (err) {
      setToast({ type: "error", message: err.message || "Gagal memuat cabang" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function save(form) {
    try {
      let res;
      if (form.id) {
        res = await api.updateBranch(form.id, { name: form.name, isActive: form.isActive });
      } else {
        res = await api.createBranch({
          code: form.code,
          name: form.name,
          cloneCatalogFrom: form.cloneCatalog ? 1 : null // 1 = CBG-01 (cabang default)
        });
      }
      setToast({ type: res.success ? "success" : "error", message: res.message || "Tersimpan" });
      if (res.success) {
        setModal(null);
        load();
      }
    } catch (err) {
      setToast({ type: "error", message: err.message || "Gagal menyimpan" });
    }
  }

  async function toggleActive(b) {
    try {
      const res = await api.updateBranch(b.id, { isActive: !b.isActive });
      setToast({ type: res.success ? "success" : "error", message: res.message });
      if (res.success) load();
    } catch (err) {
      setToast({ type: "error", message: err.message || "Gagal" });
    }
  }

  return (
    <>
      <PageTitle
        title="Kelola Cabang"
        subtitle="Buat & kelola cabang. Menonaktifkan cabang menyembunyikannya dari pemilihan — data lama tetap aman."
        action={
          <Button onClick={() => setModal({})}>
            <Icon name="add_business" />
            Tambah Cabang
          </Button>
        }
      />

      {loading ? (
        <Spinner />
      ) : (
        <DataCard>
          <table className="w-full min-w-[640px] whitespace-nowrap text-left">
            <thead>
              <tr>
                {["KODE", "NAMA", "STATUS", "MURID", "TRAINER/USER", "ACTIONS"].map((h) => (
                  <Th key={h} right={h === "ACTIONS"}>
                    {h}
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr
                  key={b.id}
                  className="border-t border-slate-200 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-primary/5"
                >
                  <Td mono primary>
                    {b.code}
                  </Td>
                  <Td>{b.name || "-"}</Td>
                  <Td>
                    {b.isActive ? (
                      <Badge>Aktif</Badge>
                    ) : (
                      <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-400 dark:bg-slate-800">
                        Nonaktif
                      </span>
                    )}
                  </Td>
                  <Td>{b.studentCount}</Td>
                  <Td>{b.userCount}</Td>
                  <Td right>
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" onClick={() => setModal(b)}>
                        <Icon name="edit" />
                        Edit
                      </Button>
                      <Button
                        variant={b.isActive ? "danger" : "primary"}
                        onClick={() => toggleActive(b)}
                      >
                        <Icon name={b.isActive ? "visibility_off" : "visibility"} />
                        {b.isActive ? "Nonaktifkan" : "Aktifkan"}
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <Td colSpan="6">Belum ada cabang.</Td>
                </tr>
              )}
            </tbody>
          </table>
        </DataCard>
      )}

      <BranchModal
        open={!!modal}
        branch={modal || {}}
        onClose={() => setModal(null)}
        onSave={save}
      />
    </>
  );
}

function BranchModal({ open, branch, onClose, onSave }) {
  const [form, setForm] = useState(branch);

  useEffect(() => {
    setForm(branch);
  }, [branch]);

  const isEdit = !!form.id;

  function patch(key, value) {
    setForm({ ...form, [key]: value });
  }

  return (
    <Modal title={isEdit ? "Edit Cabang" : "Tambah Cabang"} open={open} onClose={onClose}>
      <div className="grid gap-4">
        <Field label="Kode Cabang">
          <Input
            value={form.code || ""}
            disabled={isEdit}
            placeholder="mis. CBG-02"
            onChange={(e) => patch("code", e.target.value)}
          />
          {isEdit && (
            <p className="mt-1 text-xs text-slate-400">
              Kode tidak dapat diubah (menjaga referensi data).
            </p>
          )}
        </Field>

        <Field label="Nama Cabang">
          <Input
            value={form.name || ""}
            placeholder="mis. Cabang Sungai Raya"
            onChange={(e) => patch("name", e.target.value)}
          />
        </Field>

        {isEdit ? (
          <label className="flex cursor-pointer items-center gap-2 text-sm font-bold text-slate-500">
            <input
              type="checkbox"
              checked={form.isActive !== false}
              onChange={(e) => patch("isActive", e.target.checked)}
              className="rounded text-primary focus:ring-primary"
            />
            Cabang aktif (muncul di pemilihan)
          </label>
        ) : (
          <label className="flex cursor-pointer items-center gap-2 text-sm font-bold text-slate-500">
            <input
              type="checkbox"
              checked={!!form.cloneCatalog}
              onChange={(e) => patch("cloneCatalog", e.target.checked)}
              className="rounded text-primary focus:ring-primary"
            />
            Salin tarif & katalog dari Cabang Pusat (CBG-01) sebagai titik awal
          </label>
        )}
      </div>

      <Button className="mt-5 w-full" onClick={() => onSave(form)}>
        {isEdit ? "Simpan Perubahan" : "Buat Cabang"}
      </Button>
    </Modal>
  );
}
